import { useEffect, useRef, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import useSnackBarContext from '@contexts/snackBarContext'
import useDrawerContext from '@contexts/drawerContext'
import { useAuthContext } from '@contexts/authContext'
import useModalContext from '@contexts/modalContext'
import useLoading from '@hooks/useLoading'
import snakecaseKeys from 'snakecase-keys'
import axios from '@utils/webRequests'
import styled from '@emotion/styled'
import SignupModalDisplayNameHandle from './SignupModalDisplayNameHandle'
import { BackgroundImage } from './landing/LandingElements'
import SignupModalEmailPass from './SignupModalEmailPass'
import ModalContainer from '@elements/ModalContainer'

const SignupModalContainer = styled.div`
  position: relative;
  width: 100dvw;
  height: 100dvh;
`

const SignupModal = ({ isOpen }) => {
  const { state: authState, registerEmailPass } = useAuthContext()
  const { closeBottomDrawer, isBottomDrawerOpen } = useDrawerContext()
  const [searchParams, setSearchParams] = useSearchParams()
  const { addSnackBar } = useSnackBarContext()
  const { registerUser } = useModalContext()
  const navigate = useNavigate()
  const navigatePostCloseRef = useRef('')
  const { isLoading: isEmailPassLoading, setIsLoading: setIsEmailPassLoading, isLoadingDelayed: isEmailPassLoadingDelayed } = useLoading(false)
  const { isLoading: isSubmitLoading, setIsLoading: setIsSubmitLoading, isLoadingDelayed: isSubmitLoadingDelayed } = useLoading(false)
  const [displayHandleFormErrors, setDisplayHandleFormErrors] = useState({})
  const [emailPassFormErrors, setEmailPassFormErrors] = useState({})
  const [startedClosing, setStartedClosing] = useState(false)
  const [formState, setFormState] = useState({
    email: '',
    password: '',
    displayName: '',
    handle: '',
  })

  const currentStep = searchParams.get('step') || 0

  // WARNING: We're going through these steps because the multi-back navigations
  // causes all the previous screen to render before closing... so this hides them
  // for the brief moments the user would otherwise see them all
  useEffect(() => {
    if (startedClosing) {
      if (navigatePostCloseRef.current) {
        navigate(navigatePostCloseRef.current)
      } else {
        let backNavigate = 1
        try { backNavigate = parseInt(currentStep) + 1 } catch {}
        backNavigate = 0 - backNavigate
        navigate(backNavigate)
      }
      navigatePostCloseRef.current = ''
      setStartedClosing(false)
    }
  }, [startedClosing])

  useEffect(() => {
    if (isBottomDrawerOpen && registerUser.isOpen) {
      // WARNING: This is pretty hacky and stupid, but if you navigate away from the modal, and then backnavigate back to the modal
      //          while the bottom drawer is open, the drawer won't reopen in the correct position relative to other contents.
      //          By making the bottom drawer disappear, it causes the bottom drawer to reopen correctly after backnavigation.
      //          It's not clear that this will be an issue in other contexts where the bottom drawer doesn't have to compete with other
      //          modals for stacking priority.
      closeBottomDrawer({ useStrictNavigation: true, replace: true })
    }
  }, [isBottomDrawerOpen, registerUser.isOpen])

  const handleChange = (e) => {
    setFormState({ ...formState, [e.target.name]: e.target.value })
  }

  const setHandle = (handle) => {
    setFormState({ ...formState, handle })
  }

  const navigateToWelcome = () => {
    navigate('?modal-welcome=true&remove_background=true')
  }

  const setHandleErrors = (handleErrors) => {
    const newFormErrors = {...displayHandleFormErrors}
    if (handleErrors) {
      newFormErrors.handle = handleErrors
    } else {
      delete newFormErrors.handle
    }
    setDisplayHandleFormErrors(newFormErrors)
  }
  
  const setPasswordErrors = (passwordErrors) => {
    const newFormErrors = {...emailPassFormErrors}
    if (passwordErrors) {
      newFormErrors.password = passwordErrors
    } else {
      delete newFormErrors.password
    }
    setEmailPassFormErrors(newFormErrors)

  }

  const emailPassActionCallback = (e) => {
    e.preventDefault()
    setEmailPassFormErrors({})
    setIsEmailPassLoading(true)
    // 1) Submit request to validate email and password
    axios.post(`/api/auth/account_validations`, snakecaseKeys(formState))
      .then((res) => {
        setIsEmailPassLoading(false)
        const emailPassErrors = {}
        if (res.data.email) emailPassErrors.email = res.data.email
        if (res.data.password) emailPassErrors.password = res.data.password
        if (Object.keys(emailPassErrors).length === 0) {
          searchParams.set('step', 1)
          setSearchParams(searchParams)
        } else {
          setEmailPassFormErrors(emailPassErrors)
        }
      })
      .catch((err) => {
        console.log(err)
        setIsEmailPassLoading(false)
        addSnackBar({ message: 'Could not validate form', kind: 'error' })
      })
  }

  // TODO: refactor to handleAnonymousRegister in auth context
  const signupUser = (e) => {
    e.preventDefault()
    console.log(authState?.user)
    if (authState?.user) {
      navigateToWelcome()
      return;
    }
    setIsSubmitLoading(true)
    registerEmailPass(snakecaseKeys(formState))
      .then(() => {
        setDisplayHandleFormErrors({})
        setIsSubmitLoading(false)
        navigateToWelcome()
      })
      .catch((err) => {
        setIsSubmitLoading(false)
        setDisplayHandleFormErrors(err?.response?.data?.errors)
      })
  }

  return (
    <ModalContainer open={isOpen}>
      <SignupModalContainer style={{ minHeight: document.body.offsetHeight }}>
        <BackgroundImage src='https://mandrake-app.b-cdn.net/dev/application/display_elements/display-background-2-480w.jpeg' />
        {(!currentStep || currentStep == 0) &&
          <SignupModalEmailPass
            formState={formState}
            formErrors={emailPassFormErrors}
            handleChange={handleChange}
            isLoading={isEmailPassLoading}
            isLoadingDelayed={isEmailPassLoadingDelayed}
            actionHandler={emailPassActionCallback}
            setPasswordErrors={setPasswordErrors}
          />
        }
        {currentStep == 1 &&
          <SignupModalDisplayNameHandle
            formState={formState}
            formErrors={displayHandleFormErrors}
            setHandle={setHandle}
            setHandleErrors={setHandleErrors}
            handleChange={handleChange}
            isLoading={isSubmitLoading}
            isLoadingDelayed={isSubmitLoadingDelayed}
            actionHandler={signupUser}
          />
        }
      </SignupModalContainer>
    </ModalContainer>
  )
}

export default SignupModal
